// src/store/usePhotoSlotsStore.ts
import { create }  from 'zustand'
import { persist } from 'zustand/middleware'
import axios       from 'axios'
import { PhotoSlot, DEFAULT_PHOTO_SLOTS, setLivePhotoSlots } from '../utils/constants'
import { getStoreAuthHeaders } from '../utils/auth'
import { BACKEND } from '../utils/config'

// ── Types ──────────────────────────────────────────────────────────────────────
interface PhotoSlotsState {
  photoSlots:   PhotoSlot[]
  slotsLoaded:  boolean
  slotsLoading: boolean
  slotsError:   string | null

  fetchSlots:   ()                                              => Promise<void>
  addSlot:      (slot: Omit<PhotoSlot, 'order'>)               => Promise<void>
  updateSlot:   (id: string, patch: Partial<Omit<PhotoSlot, 'id'>>) => Promise<void>
  removeSlot:   (id: string)                                   => Promise<void>
  reorderSlots: (ordered: PhotoSlot[])                         => Promise<void>

  _authHeader:  () => Record<string, string>
}

// ── Store ──────────────────────────────────────────────────────────────────────
export const usePhotoSlotsStore = create<PhotoSlotsState>()(
  persist(
    (set, get) => ({

      photoSlots:   [...DEFAULT_PHOTO_SLOTS],
      slotsLoaded:  false,
      slotsLoading: false,
      slotsError:   null,

      // ── Auth header — via store Zustand (plus de localStorage hack) ────────
      _authHeader: (): Record<string, string> => {
        try {
          return getStoreAuthHeaders()
        } catch { return {} }
      },

      // ── GET /api/config/slots ─────────────────────────────────────────────
      fetchSlots: async () => {
        set({ slotsLoading: true, slotsError: null })
        try {
          const { data } = await axios.get<PhotoSlot[]>(
            `${BACKEND}/api/config/slots`,
            { headers: get()._authHeader() }
          )
          const sorted = [...data].sort((a, b) => a.order - b.order)
          set({ photoSlots: sorted, slotsLoaded: true, slotsLoading: false })
          setLivePhotoSlots(sorted)
        } catch (err: any) {
          console.warn('[fetchSlots]', err.message)
          set({ slotsLoaded: true, slotsLoading: false, slotsError: err.message })
          setLivePhotoSlots(get().photoSlots) // fallback cache persist
        }
      },

      // ── POST /api/config/slots ────────────────────────────────────────────
      addSlot: async (slot) => {
        const newSlot: PhotoSlot = {
          ...slot,
          order: get().photoSlots.length,
        }
        const { data } = await axios.post<PhotoSlot>(
          `${BACKEND}/api/config/slots`,
          newSlot,
          { headers: { 'Content-Type': 'application/json', ...get()._authHeader() } }
        )
        const updated = [...get().photoSlots, data]
        set({ photoSlots: updated })
        setLivePhotoSlots(updated)
      },

      // ── PUT /api/config/slots/:id ─────────────────────────────────────────
      updateSlot: async (id, patch) => {
        await axios.put(
          `${BACKEND}/api/config/slots/${id}`,
          patch,
          { headers: { 'Content-Type': 'application/json', ...get()._authHeader() } }
        )
        const updated = get().photoSlots.map(s => s.id === id ? { ...s, ...patch } : s)
        set({ photoSlots: updated })
        setLivePhotoSlots(updated)
      },

      // ── DELETE /api/config/slots/:id ──────────────────────────────────────
      removeSlot: async (id) => {
        await axios.delete(
          `${BACKEND}/api/config/slots/${id}`,
          { headers: get()._authHeader() }
        )
        const updated = get().photoSlots
          .filter(s => s.id !== id)
          .map((s, i) => ({ ...s, order: i }))
        set({ photoSlots: updated })
        setLivePhotoSlots(updated)
      },

      // ── POST /api/config/slots/reorder ────────────────────────────────────
      reorderSlots: async (ordered) => {
        const reindexed = ordered.map((s, i) => ({ ...s, order: i }))
        // Optimiste : UI mise à jour immédiatement
        set({ photoSlots: reindexed })
        setLivePhotoSlots(reindexed)
        await axios.post(
          `${BACKEND}/api/config/slots/reorder`,
          { slots: reindexed },
          { headers: { 'Content-Type': 'application/json', ...get()._authHeader() } }
        )
      },

    }),
    {
      name:       'rentalcheck-photo-slots-v1',         // clé localStorage distincte
      partialize: (state) => ({
        photoSlots:  state.photoSlots,
        slotsLoaded: state.slotsLoaded,
      }),
    }
  )
)